import { chattelDetailsApi } from 'app/services/api';
import _ from 'lodash';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectChattelDetails } from '../selectors/chattelDetails.selectors';

import { getLoanApplicationCommonDataByApplicationIdAction } from './loanApplication.actions';
import {
  getLoanAndLockSummaryEditDataAction,
  getPropertyAndLoanSummaryAction,
} from './propertyAndLoan.actions';

// A chattel loan amount is derived from its sales contract (LEN-127): the backend recalculates the
// loan when one of these changes, so the loan data shown alongside has to be read again.
const CHATTEL_SALES_CONTRACT_FIELDS = ['salesPrice', 'salesTax', 'mhit'];

const checkIsChattelSalesContractChanged = ({ previous, next }) =>
  CHATTEL_SALES_CONTRACT_FIELDS.some(
    field => Number(previous?.[field] ?? 0) !== Number(next?.[field] ?? 0),
  );

export const CHATTEL_DETAILS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/chattelDetails`,
  {
    GET_CHATTEL_DETAILS: 'GET_CHATTEL_DETAILS',
    UPDATE_CHATTEL_DETAILS: 'UPDATE_CHATTEL_DETAILS',
  },
);

// A chattel loan has no ChattelDetails row until the first save (created lazily), so the GET
// returns an empty body for a brand-new chattel loan (204 No Content, or 404 on some paths).
// Normalize both to null so callers render/handle a "no data yet" state rather than letting it
// bubble up and fail the whole Property & Loan tab's Promise.all fetch.
export const fetchChattelDetails = async params => {
  try {
    const { data } = await chattelDetailsApi.getChattelDetails(params);

    return _.isEmpty(data) ? null : data;
  } catch (error) {
    if (error?.response?.status !== HTTP_RESPONSE_STATUS_CODES.NOT_FOUND) {
      throw error;
    }

    return null;
  }
};

export const getChattelDetailsAction = catchAsyncActionError(params => async dispatch => {
  const chattelDetails = await fetchChattelDetails(params);

  return dispatch({
    type: CHATTEL_DETAILS_ACTION_TYPE.GET_CHATTEL_DETAILS,
    payload: chattelDetails,
  });
});

// The first save on a brand-new chattel loan must POST to CREATE the row -- the backend PUT
// (update) handler 404s on a missing row instead of upserting. Once a row exists, PUT updates it.
// Row existence is read from the store: getChattelDetailsAction seeds `details` as null when there
// is no row yet (empty GET), and as the DTO once one exists.
export const saveChattelDetailsAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const previous = selectChattelDetails(getState());
    const hasExistingRow = !_.isEmpty(previous);

    const { data } = hasExistingRow
      ? await chattelDetailsApi.updateChattelDetails(params)
      : await chattelDetailsApi.createChattelDetails(params);

    const result = dispatch({
      type: CHATTEL_DETAILS_ACTION_TYPE.UPDATE_CHATTEL_DETAILS,
      payload: data,
    });

    if (checkIsChattelSalesContractChanged({ previous, next: data })) {
      const { applicationId } = params;

      // Best effort: the save itself succeeded, so a failed reload must not surface as an error.
      await Promise.all([
        dispatch(getLoanAndLockSummaryEditDataAction({ applicationId })),
        dispatch(getPropertyAndLoanSummaryAction({ applicationId })),
        dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId })),
      ]).catch(_.noop);
    }

    return result;
  },
);

// LEN-126: Loan Details edits only PurposeOfChattel, so it goes through the purpose-only endpoint rather than
// re-sending the whole chattel-details document: PUT /chattel-details re-validates every field, and a POS-created
// row can fail an unrelated rule (e.g. in a park with no park name), which blocked saving just the purpose.
export const saveChattelPurposeAction =
  ({ applicationId, purposeOfChattel }) =>
  async dispatch => {
    const { data } = await chattelDetailsApi.setPurposeOfChattel({
      applicationId,
      purposeOfChattel,
    });

    return dispatch({
      type: CHATTEL_DETAILS_ACTION_TYPE.UPDATE_CHATTEL_DETAILS,
      payload: data,
    });
  };
